// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import {
  Field,
  type FormProps,
  formValueSelector,
  reduxForm,
} from 'redux-form';

import type { Catalog, DiskPreviewInfo, Dispatch } from 'state/types';
import { getDiskPreviewInfo } from 'state/selectors';
import {
  getCatalogProperties as getCatalogPropertiesAction,
  updateDiskPreviewInfo,
} from 'state/commands/catalogCommands';
import localizedStrings from 'util/localizedStrings';
import { CATALOG_PROPERTY_KEY } from 'util/constants';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import { BROWSE_SERVER_FOLDERS_PANEL_ID } from 'util/constants';
import BrowseServerFoldersDialog from 'view/App/DialogPanels/BrowseServerFoldersDialog';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import CheckRadio from 'view/Forms/BulmaCheckRadio';

const MIN_DIMENSION = 300;
const MAX_DIMENSION = 4000;
const MIN_DURATION = 1;
const MAX_DURATION = 180;

const AV_RES = [
  { key: '360p', value: 360 },
  { key: '480p', value: 480 },
  { key: '540p HD', value: 540 },
  { key: '720p HD', value: 720 },
  { key: '1080p HD', value: 1080 },
];

type Props = {
  catalog: Catalog,
  diskPreviewInfo: DiskPreviewInfo,
  getCatalogPropertiesAction: typeof getCatalogPropertiesAction,
  intl: IntlShape,
  showDialogPanel: typeof showDialogPanel,
  updateDiskPreviewInfo: typeof updateDiskPreviewInfo,
} & FormProps;

class CatalogPreviewsForm extends Component<Props> {
  handleBrowsePreviewsFolder: Function;
  handlePreviewFolderPathSelected: Function;
  initializeFields: Function;
  submit: Function;

  constructor() {
    super();
    this.handleBrowsePreviewsFolder = this.handleBrowsePreviewsFolder.bind(
      this
    );
    this.initializeFields = this.initializeFields.bind(this);
    this.submit = this.submit.bind(this);
    this.handlePreviewFolderPathSelected = this.handlePreviewFolderPathSelected.bind(
      this
    );
  }

  componentDidMount() {
    if (this.props.catalog) {
      this.props.getCatalogPropertiesAction(this.props.catalog.catalogId, [
        CATALOG_PROPERTY_KEY.DISK_PREVIEW_INFO,
        CATALOG_PROPERTY_KEY.CATALOG_DETAILS,
      ]);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.diskPreviewInfo !== nextProps.diskPreviewInfo) {
      this.initializeFields(nextProps.diskPreviewInfo);
    }
  }

  initializeFields(diskPreviewInfo: DiskPreviewInfo) {
    this.props.initialize(diskPreviewInfo);
  }

  async submit(values) {
    const {
      catalog,
      intl: { formatMessage },
      updateDiskPreviewInfo,
    } = this.props;
    const {
      audioVideoPreviewsEnabled,
      enabled,
      jpegCompression,
      limitVideoDurationEnabled,
      maxDimension,
      maxVideoDuration,
      maxVideoResolution,
      path,
    } = values;
    const dpi: DiskPreviewInfo = {
      audioVideoPreviewsEnabled,
      enabled,
      jpegCompression,
      limitVideoDurationEnabled,
      maxDimension,
      maxVideoDuration,
      maxVideoResolution,
      path,
    };
    const successMessage = formatMessage(
      localizedStrings.NOTIFICATION_SUCCESS_UPDATE
    );
    await updateDiskPreviewInfo(catalog.catalogId, dpi, successMessage);
  }

  handleBrowsePreviewsFolder() {
    this.props.showDialogPanel(BROWSE_SERVER_FOLDERS_PANEL_ID);
  }

  handlePreviewFolderPathSelected(selectedPath) {
    this.props.change('path', selectedPath);
  }

  render() {
    const {
      diskPreviewInfo,
      handleSubmit,
      intl: { formatMessage },
      invalid,
      pristine,
      reset,
    } = this.props;

    return !diskPreviewInfo ? (
      ''
    ) : (
      <div
        className="portfolio-form-container page-contents-textured"
        data-test-name="catalog-preview-page"
      >
        <BrowseServerFoldersDialog
          onPathSelected={this.handlePreviewFolderPathSelected}
        />
        <div className="box">
          <form style={{ width: '700px' }} onSubmit={handleSubmit(this.submit)}>
            <div className="field">
              <div className="label">
                {formatMessage(localizedStrings.CATALOG_PREVIEWS_MAX_PIXELS)}
              </div>

              <div className="field is-expanded">
                <div className="field has-addons">
                  <div className="control">
                    <Field
                      className="input"
                      component="input"
                      id="maxDimension"
                      max={MAX_DIMENSION}
                      min={MIN_DIMENSION}
                      name="maxDimension"
                      style={{ width: '100px' }}
                      type="number"
                    />
                  </div>
                  <p className="control">
                    <a className="button is-static">
                      {formatMessage(localizedStrings.CATALOG_PREVIEWS_PIXELS)}
                    </a>
                  </p>
                </div>
                <p className="help">
                  {formatMessage(
                    localizedStrings.CATALOG_PREVIEWS_DIMENSIONS_LABEL
                  )}
                </p>
              </div>
            </div>

            <div className="vertical-spacer-large" />

            <div className="field">
              <div className="control">
                <Field
                  component={CheckRadio}
                  id="audioVideoPreviewsEnabled"
                  label={formatMessage(
                    localizedStrings.CATALOG_PREVIEWS_GENERATE_AUDIO_VIDEO_FILES
                  )}
                  name="audioVideoPreviewsEnabled"
                  type="checkbox"
                />
              </div>
            </div>

            <div className="field is-indented">
              <div className="label">
                {formatMessage(
                  localizedStrings.CATALOG_PREVIEWS_MAX_VIDEO_RESOLUTION
                )}
              </div>
              <div className="field">
                <div className="select">
                  <Field
                    className="select"
                    component="select"
                    id="maxVideoResolution"
                    name="maxVideoResolution"
                  >
                    {AV_RES.map(res => (
                      <option
                        key={res.key}
                        value={res.value}
                        data-test-name="max-video-resolution-option"
                      >
                        {res.key}
                      </option>
                    ))}
                  </Field>
                </div>
              </div>
            </div>

            <div className="field is-indented">
              <div className="control">
                <Field
                  component={CheckRadio}
                  id="limitVideoDurationEnabled"
                  label={formatMessage(
                    localizedStrings.CATALOG_PREVIEWS_VIDEO_LIMIT_DURATION
                  )}
                  name="limitVideoDurationEnabled"
                  type="checkbox"
                />
                <div
                  className="field has-addons"
                  style={{ marginTop: '5px', marginLeft: '0.5rem' }}
                >
                  <div className="control">
                    <Field
                      className="input"
                      component="input"
                      id="maxVideoDuration"
                      max={MAX_DURATION}
                      min={MIN_DURATION}
                      name="maxVideoDuration"
                      style={{ width: '100px' }}
                      type="number"
                    />
                  </div>
                  <p className="control">
                    <a className="button is-static">
                      {formatMessage(localizedStrings.CATALOG_PREVIEWS_MINUTES)}
                    </a>
                  </p>
                </div>
              </div>
              <p className="help">
                {formatMessage(
                  localizedStrings.CATALOG_PREVIEWS_VIDEO_CLIP_PROCESSING_INFO_TEXT
                )}
              </p>
            </div>

            <div className="vertical-spacer-large" />

            <div className="field">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                }}
              >
                <div className="label">
                  {formatMessage(
                    localizedStrings.CATALOG_PREVIEWS_FOLDER_LOCATION
                  )}
                </div>
                <a
                  className="button is-text"
                  onClick={this.handleBrowsePreviewsFolder}
                  data-test-name="browser-for-previews-folder"
                >
                  {formatMessage(localizedStrings.BROWSE)}
                </a>
              </div>
              <div className="field">{this.props.path}</div>
            </div>

            <div className="vertical-spacer-large" />

            <div className="field is-grouped is-pulled-right">
              <div className="control">
                <button
                  className="button is-dark is-outlined"
                  disabled={pristine}
                  onClick={reset}
                  type="button"
                  data-test-name="reset"
                >
                  <FormattedMessage {...localizedStrings.REVERT} />
                </button>
              </div>
              <div className="control">
                <button
                  className="button is-primary"
                  type="submit"
                  disabled={pristine | invalid}
                  data-test-name="submit"
                >
                  <FormattedMessage {...localizedStrings.APPLY} />
                </button>
              </div>
            </div>

            <div className="vertical-spacer-xlarge" />
          </form>
        </div>
      </div>
    );
  }
}

function validate(data: Object, props: Props) {
  const errors = {};
  if (data.maxDimension < MIN_DIMENSION || data.maxDimension > MAX_DIMENSION) {
    errors.maxDimension = 'Invalid';
  }
  if (
    data.maxVideoDuration < MIN_DURATION ||
    data.maxVideoDuration > MAX_DIMENSION
  ) {
    errors.maxVideoDuration = 'Invalid';
  }
  return errors;
}

const form = reduxForm({
  destroyOnUnmount: false,
  form: 'CatalogPreviewsForm',
  validate,
})(injectIntl(CatalogPreviewsForm));

const previewsFormSelector = formValueSelector('CatalogPreviewsForm');

export default connect(
  state => ({
    diskPreviewInfo: getDiskPreviewInfo(state),
    path: previewsFormSelector(state, 'path'),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      getCatalogPropertiesAction,
      showDialogPanel,
      updateDiskPreviewInfo,
    })
)(form);



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CatalogPreviewsForm.js